import { useMemo, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useStudio } from "@/store/StudioContext";
import { inspectAssetsUsage } from "@/domain/assetUsage";
import { api } from "@/lib/api";
import { ShieldAlert, AlertTriangle } from "lucide-react";

/**
 * Diálogo unificado para gestionar/eliminar recursos con protección estricta de medios en uso.
 *  - "Quitar de la escena": solo desvincula si se especifica sceneId.
 *  - "Eliminar definitivamente":
 *      - Identifica recursos en uso (escenas, referencias, personajes, nodos) y bloquea su eliminación
 *        para proteger la integridad de los proyectos.
 *      - Informa qué recursos están protegidos y por qué.
 *      - Permite eliminar con confirmación únicamente los recursos que no están en uso.
 *      - Soporta fallos parciales conservando seleccionados los recursos no eliminados.
 *      - Protege contra dobles pulsaciones durante el envío.
 */
const DeleteAssetDialog = ({ open, assetIds = [], sceneId = null, onClose, onDone }) => {
  const { state, dispatch } = useStudio();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const { protectedAssets, deletableAssetIds, isAllProtected, isPartiallyProtected } = useMemo(() => {
    if (!open || !assetIds || assetIds.length === 0) {
      return { protectedAssets: [], deletableAssetIds: [], isAllProtected: false, isPartiallyProtected: false };
    }
    return inspectAssetsUsage(state, assetIds, sceneId);
  }, [open, state, assetIds, sceneId]);

  const contextScene = sceneId ? state.scenes.find((s) => s.id === sceneId) : null;
  const linkedToContext = Boolean(contextScene) && assetIds.every((id) =>
    (contextScene.assetIds || []).includes(id) || state.assets.find((a) => a.id === id)?.sceneId === contextScene.id
  );

  const total = assetIds.length;
  const plural = total !== 1;

  const close = () => {
    if (busy) return;
    setError(null);
    onClose?.();
  };

  const removeFromScene = () => {
    if (busy) return;
    setBusy(true);
    try {
      assetIds.forEach((id) => dispatch({ type: "SCENE_REMOVE_ASSET", payload: { sceneId, assetId: id } }));
      onDone?.({ action: "removed", assetIds });
      close();
    } finally {
      setBusy(false);
    }
  };

  const deleteForever = async () => {
    if (busy || deletableAssetIds.length === 0) return;
    setBusy(true);
    setError(null);

    try {
      const res = await api.deleteAssets(deletableAssetIds);
      const deleted = Array.isArray(res?.deleted) ? res.deleted : deletableAssetIds;
      const notDeleted = deletableAssetIds.filter((id) => !deleted.includes(id));

      if (deleted.length > 0) {
        dispatch({ type: "ASSETS_DELETE", payload: { ids: deleted } });
      }

      if (notDeleted.length > 0) {
        setError(
          `Se eliminaron ${deleted.length} recursos. ${notDeleted.length} no pudieron eliminarse y siguen seleccionados.`
        );
        onDone?.({
          deleted,
          failed: notDeleted,
          protected: protectedAssets.map((p) => p.id),
        });
      } else {
        onDone?.({
          deleted,
          failed: [],
          protected: protectedAssets.map((p) => p.id),
        });
        close();
      }
    } catch (err) {
      setError(
        err?.status === 401
          ? "Tu sesión ha caducado. Inicia sesión de nuevo."
          : "No se pudo completar la eliminación. Ningún recurso protegido fue modificado."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent
        data-testid="delete-asset-dialog"
        className="max-w-lg border-[#303030] bg-[#1b1b1b] p-6 text-[#f4f1ec]"
      >
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-base font-semibold">
            {plural ? `Gestionar ${total} recursos seleccionados` : "Gestionar recurso"}
          </h3>
          <span className="font-mono text-xs text-[#a3a3a3]">
            {total} {plural ? "recursos" : "recurso"}
          </span>
        </div>

        {/* ── Opción de desvincular de la escena de contexto ── */}
        {linkedToContext && (
          <div className="mt-3 rounded-md border border-[#303030] p-3">
            <p className="text-sm font-medium">Quitar de la escena</p>
            <p className="mt-1 text-xs text-[#a3a3a3]">
              Se desvincula de «{contextScene.title || "Sin título"}». El archivo se conserva en la biblioteca y la Galería.
            </p>
            <button
              data-testid="delete-asset-remove-from-scene"
              onClick={removeFromScene}
              disabled={busy}
              className="v-btn-ghost v-btn-sm mt-2"
            >
              {busy ? "Procesando…" : "Quitar de la escena"}
            </button>
          </div>
        )}

        {/* ── Aviso detallado de medios protegidos en uso ── */}
        {protectedAssets.length > 0 && (
          <div
            data-testid="delete-asset-protected-warning"
            className="mt-3 rounded-md border border-amber-500/40 bg-amber-950/20 p-3 text-xs text-amber-200"
          >
            <div className="flex items-start gap-2">
              <ShieldAlert className="mt-0.5 shrink-0 text-amber-400" size={16} />
              <div>
                <p className="font-semibold text-amber-300">
                  {isAllProtected
                    ? "Recursos protegidos — en uso en el proyecto"
                    : `${protectedAssets.length} de ${total} recursos protegidos`}
                </p>
                <p className="mt-1 text-amber-200/90 leading-relaxed">
                  {isAllProtected
                    ? "Ninguno de los recursos seleccionados puede eliminarse porque todos están utilizados en escenas, personajes o referencias activas. Sus archivos e integridad quedan protegidos."
                    : `${protectedAssets.length} ${
                        protectedAssets.length === 1 ? "recurso está protegido" : "recursos están protegidos"
                      } porque se utiliza en escenas, referencias o personajes. No se eliminará ni se desvinculará silenciosamente.`}
                </p>
              </div>
            </div>

            <div
              data-testid="delete-asset-protected-list"
              className="mt-2.5 max-h-40 overflow-y-auto space-y-1.5 rounded border border-amber-500/20 bg-[#121212]/70 p-2 text-[11px]"
            >
              {protectedAssets.map(({ id, asset, reasons }) => (
                <div key={id} data-testid={`protected-item-${id}`} className="border-b border-[#262626] pb-1 last:border-b-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-amber-200 truncate">{asset.label || asset.id}</span>
                    <span className="font-mono text-[10px] text-amber-400/80 shrink-0">Protegido</span>
                  </div>
                  <div className="text-[#a3a3a3] mt-0.5 leading-snug">
                    {reasons.join(" · ")}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Panel de eliminación definitiva para recursos no utilizados ── */}
        <div className="mt-3 rounded-md border border-red-500/30 p-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="text-red-400 shrink-0" size={16} />
            <p className="text-sm font-medium text-red-300">Eliminar definitivamente</p>
          </div>

          {deletableAssetIds.length > 0 ? (
            <>
              <p className="mt-1.5 text-xs text-[#a3a3a3] leading-relaxed">
                {isPartiallyProtected
                  ? `Se eliminarán del servidor únicamente las ${deletableAssetIds.length} imágenes no utilizadas. Esta acción es irreversible.`
                  : `Borra ${
                      deletableAssetIds.length === 1 ? "el archivo" : `los ${deletableAssetIds.length} archivos`
                    } del servidor. No se puede deshacer. Las versiones derivadas no se eliminan.`}
              </p>
              <button
                data-testid="delete-asset-confirm"
                onClick={deleteForever}
                disabled={busy}
                className="v-btn-sm mt-3 rounded border border-red-500/60 bg-red-500/10 px-4 text-red-400 hover:bg-red-500/20 disabled:opacity-50"
              >
                {busy
                  ? "Eliminando…"
                  : isPartiallyProtected
                  ? `Eliminar ${deletableAssetIds.length} no utilizado${deletableAssetIds.length !== 1 ? "s" : ""}`
                  : `Eliminar ${deletableAssetIds.length} definitivamente`}
              </button>
            </>
          ) : (
            <p className="mt-1.5 text-xs text-[#a3a3a3]">
              No hay recursos libres para eliminar en la selección actual.
            </p>
          )}
        </div>

        {error && (
          <p data-testid="delete-asset-error" className="mt-3 text-xs text-red-400">
            {error}
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            data-testid="delete-asset-cancel"
            onClick={close}
            disabled={busy}
            className="v-btn-ghost v-btn-sm"
          >
            {isAllProtected ? "Cerrar" : "Cancelar"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteAssetDialog;
